import { useState, useEffect } from 'react';
import { Plus, Search, Wrench, Edit, Eye, Trash2, Calendar, DollarSign, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface MaintenanceLog {
  id: string;
  log_number: string;
  maintenance_type: string;
  truck_id: string;
  trailer_id?: string;
  unit_type?: string;
  service_provider: string | null;
  service_date: string;
  cost: number;
  company_cost_amount: number;
  gl_account: string;
  paid_by?: string;
  driver_payment_amount?: number;
  repair_location?: string;
  trucks?: {
    unit_number: string;
    make: string;
    model: string;
  };
  trailers?: {
    unit_number: string;
    make: string;
    model: string;
  };
}

interface MaintenanceLogsViewProps {
  onCreateNew: () => void;
  onViewDetails: (logId: string) => void;
  onEdit: (logId: string) => void;
}

export function MaintenanceLogsView({ onCreateNew, onViewDetails, onEdit }: MaintenanceLogsViewProps) {
  const toast = useToast();
  const [logs, setLogs] = useState<MaintenanceLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [unitTypeFilter, setUnitTypeFilter] = useState<'all' | 'truck' | 'trailer'>('all');
  const [unitFilter, setUnitFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'company' | 'driver'>('all');
  const [locationFilter, setLocationFilter] = useState<'all' | 'Yard' | 'Road'>('all');
  const [trucks, setTrucks] = useState<Array<{ id: string; unit_number: string }>>([]);
  const [trailers, setTrailers] = useState<Array<{ id: string; unit_number: string }>>([]);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const maintenanceTypes = [
    'Preventive Maintenance',
    'Oil Change',
    'Tire Service',
    'Brake Service',
    'Engine Repair',
    'Transmission Repair',
    'Electrical Repair',
    'Body Work',
    'DOT Inspection',
    'Annual Inspection',
    'Other',
  ];

  useEffect(() => {
    loadLogs();
    loadUnits();
  }, [typeFilter, unitTypeFilter, unitFilter, selectedMonth, locationFilter]);

  async function loadUnits() {
    try {
      // Load trucks
      const { data: trucksData } = await supabase
        .from('trucks')
        .select('id, unit_number')
        .order('unit_number');
      
      setTrucks(trucksData || []);

      // Load trailers
      const { data: trailersData } = await supabase
        .from('trailers')
        .select('id, unit_number')
        .order('unit_number');
      
      setTrailers(trailersData || []);
    } catch (error) {
      console.error('Error loading units:', error);
    }
  }

  async function loadLogs() {
    try {
      let query = supabase
        .from('maintenance_logs')
        .select(`
          *,
          trucks(unit_number, make, model),
          trailers(unit_number, make, model)
        `)
        .order('service_date', { ascending: false });

      if (typeFilter !== 'all') {
        query = query.eq('maintenance_type', typeFilter);
      }

      if (unitTypeFilter !== 'all') {
        query = query.eq('unit_type', unitTypeFilter);
      }

      if (unitFilter !== 'all') {
        if (unitTypeFilter === 'truck' || unitTypeFilter === 'all') {
          query = query.eq('truck_id', unitFilter);
        } else if (unitTypeFilter === 'trailer') {
          query = query.eq('trailer_id', unitFilter);
        }
      }

      if (selectedMonth !== 'all') {
        const [year, month] = selectedMonth.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0);
        query = query.gte('service_date', startDate.toISOString().split('T')[0]);
        query = query.lte('service_date', endDate.toISOString().split('T')[0]);
      }

      const { data, error } = await query;
      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading maintenance logs:', error);
      toast.error('Failed to load maintenance logs');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(logId: string, logNumber: string) {
    if (!confirm(`Are you sure you want to delete log ${logNumber}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('maintenance_logs')
        .delete()
        .eq('id', logId);

      if (error) throw error;

      toast.success('Maintenance log deleted successfully');
      loadLogs();
    } catch (error) {
      console.error('Error deleting log:', error);
      toast.error('Failed to delete maintenance log');
    }
  }

  const handleRowSelect = (logId: string) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === filteredLogs.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredLogs.map(log => log.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    
    if (!confirm(`Are you sure you want to delete ${selectedRows.size} maintenance log(s)?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('maintenance_logs')
        .delete()
        .in('id', Array.from(selectedRows));

      if (error) throw error;

      toast.success(`${selectedRows.size} maintenance log(s) deleted successfully`);
      setSelectedRows(new Set());
      loadLogs();
    } catch (error) {
      console.error('Error deleting logs:', error);
      toast.error('Failed to delete maintenance logs');
    }
  };

  const handleBulkEdit = () => {
    if (selectedRows.size === 0) return;
    // For now, just edit the first selected item
    const firstSelected = Array.from(selectedRows)[0];
    onEdit(firstSelected);
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' || 
      log.log_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.maintenance_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.service_provider?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.trucks?.unit_number.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPayment = paymentFilter === 'all' || 
      (paymentFilter === 'company' && log.paid_by === 'company') ||
      (paymentFilter === 'driver' && log.paid_by === 'driver');

    const matchesLocation = locationFilter === 'all' ||
      log.repair_location === locationFilter;

    return matchesSearch && matchesPayment && matchesLocation;
  });

  const stats = {
    total: filteredLogs.length,
    thisMonth: filteredLogs.filter(log => {
      const logDate = new Date(log.service_date);
      const now = new Date();
      return logDate.getMonth() === now.getMonth() && logDate.getFullYear() === now.getFullYear();
    }).length,
    driverCost: filteredLogs.reduce((sum, log) => sum + (log.driver_payment_amount || 0), 0),
    companyCost: filteredLogs.reduce((sum, log) => sum + log.company_cost_amount, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-900">Maintenance Logs</h1>
          <button
            onClick={onCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            New Maintenance Log
          </button>
        </div>
      </div>

      {/* Month Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-slate-700 whitespace-nowrap">Filter by Month:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Months</option>
            <option value="2025-12">December 2025</option>
            <option value="2025-11">November 2025</option>
            <option value="2025-10">October 2025</option>
            <option value="2025-09">September 2025</option>
            <option value="2025-08">August 2025</option>
            <option value="2025-07">July 2025</option>
            <option value="2025-06">June 2025</option>
            <option value="2025-05">May 2025</option>
            <option value="2025-04">April 2025</option>
            <option value="2025-03">March 2025</option>
            <option value="2025-02">February 2025</option>
            <option value="2025-01">January 2025</option>
            <option value="2024-12">December 2024</option>
            <option value="2024-11">November 2024</option>
            <option value="2024-10">October 2024</option>
            <option value="2024-09">September 2024</option>
            <option value="2024-08">August 2024</option>
            <option value="2024-07">July 2024</option>
            <option value="2024-06">June 2024</option>
            <option value="2024-05">May 2024</option>
            <option value="2024-04">April 2024</option>
            <option value="2024-03">March 2024</option>
            <option value="2024-02">February 2024</option>
            <option value="2024-01">January 2024</option>
          </select>
        </div>
      </div>

      <>
          {/* Stats Cards */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Wrench className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Total Logs</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  </div>
                </div>
              </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">This Month</p>
                <p className="text-2xl font-bold text-slate-900">{stats.thisMonth}</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-orange-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Driver Cost</p>
                <p className="text-2xl font-bold text-slate-900">${stats.driverCost.toFixed(0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Company Cost</p>
                <p className="text-2xl font-bold text-slate-900">${stats.companyCost.toFixed(0)}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-900">${(stats.driverCost + stats.companyCost).toFixed(0)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        {/* Payment Type and Location Filters */}
        <div className="flex items-center justify-between gap-6 mb-3">
          {/* Payment Filter Buttons */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-slate-700 whitespace-nowrap">Payment Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => setPaymentFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  paymentFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setPaymentFilter('company')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  paymentFilter === 'company'
                    ? 'bg-green-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Company Paid
              </button>
              <button
                onClick={() => setPaymentFilter('driver')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  paymentFilter === 'driver'
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Driver Paid
              </button>
            </div>
          </div>

          {/* Location Filter Buttons */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-slate-700 whitespace-nowrap">Location</label>
            <div className="flex gap-2">
              <button
                onClick={() => setLocationFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  locationFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setLocationFilter('Yard')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  locationFilter === 'Yard'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Yard
              </button>
              <button
                onClick={() => setLocationFilter('Road')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  locationFilter === 'Road'
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Road
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search logs, trucks, providers..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              {maintenanceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Unit Type Filter */}
          <div>
            <select
              value={unitTypeFilter}
              onChange={(e) => {
                setUnitTypeFilter(e.target.value as 'all' | 'truck' | 'trailer');
                setUnitFilter('all'); // Reset unit filter when type changes
              }}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Units</option>
              <option value="truck">Trucks</option>
              <option value="trailer">Trailers</option>
            </select>
          </div>

          {/* Unit Filter */}
          <div>
            <select
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">
                {unitTypeFilter === 'truck' ? 'All Trucks' : unitTypeFilter === 'trailer' ? 'All Trailers' : 'All Units'}
              </option>
              {(unitTypeFilter === 'truck' || unitTypeFilter === 'all' ? trucks : trailers).map(unit => (
                <option key={unit.id} value={unit.id}>{unit.unit_number}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <div>
            <button
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('all');
                setUnitTypeFilter('all');
                setUnitFilter('all');
                setSelectedMonth('all');
                setPaymentFilter('all');
                setLocationFilter('all');
              }}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === filteredLogs.length && filteredLogs.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Log Number
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Truck
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Service Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <Wrench className="w-8 h-8 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900">No maintenance logs found</p>
                        <p className="text-sm text-gray-500 mt-1">Create your first maintenance log to get started</p>
                      </div>
                      <button
                        onClick={onCreateNew}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Create Maintenance Log
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr
                    key={log.id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 ${
                      selectedRows.has(log.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={(e) => {
                      // Don't trigger row click if clicking on checkbox or action buttons
                      if ((e.target as HTMLElement).closest('input[type="checkbox"]') || 
                          (e.target as HTMLElement).closest('button')) {
                        return;
                      }
                      onViewDetails(log.id);
                    }}
                  >
                    <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(log.id)}
                        onChange={() => handleRowSelect(log.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-600">ML</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{log.log_number}</p>
                          <p className="text-xs text-gray-500">Maintenance Log</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
                        log.repair_location === 'Yard' 
                          ? 'bg-blue-100 text-blue-700' 
                          : log.repair_location === 'Road'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {log.repair_location || 'N/A'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          log.maintenance_type === 'Oil Change' ? 'bg-amber-400' :
                          log.maintenance_type === 'Annual Inspection' ? 'bg-green-400' :
                          log.maintenance_type === 'Tire Service' ? 'bg-orange-400' :
                          log.maintenance_type === 'Brake Service' ? 'bg-red-400' :
                          'bg-blue-400'
                        }`} />
                        <span className="text-sm font-medium text-gray-900">{log.maintenance_type}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {log.unit_type === 'both' ? (
                        <div className="flex flex-col gap-2">
                          {log.trucks && (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-blue-600">{log.trucks.unit_number.slice(-2)}</span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 text-xs">{log.trucks.unit_number}</p>
                                <p className="text-xs text-gray-500">{log.trucks.make}</p>
                              </div>
                            </div>
                          )}
                          {log.trailers && (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-bold text-purple-600">{log.trailers.unit_number.slice(-2)}</span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 text-xs">{log.trailers.unit_number}</p>
                                <p className="text-xs text-gray-500">{log.trailers.make}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : log.trucks ? (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-gray-600">{log.trucks.unit_number.slice(-2)}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{log.trucks.unit_number}</p>
                            <p className="text-xs text-gray-500">{log.trucks.make}</p>
                          </div>
                        </div>
                      ) : log.trailers ? (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-purple-600">{log.trailers.unit_number.slice(-2)}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{log.trailers.unit_number}</p>
                            <p className="text-xs text-gray-500">{log.trailers.make}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{new Date(log.service_date).toLocaleDateString()}</p>
                          <p className="text-xs text-gray-500">{new Date(log.service_date).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-gray-600">SP</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{log.service_provider || 'N/A'}</p>
                          <p className="text-xs text-gray-500">Service Provider</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">${log.cost.toFixed(2)}</p>
                          {log.driver_payment_amount && log.driver_payment_amount > 0 ? (
                            <>
                              <p className="text-xs text-orange-600">Driver: ${log.driver_payment_amount.toFixed(2)}</p>
                              <p className="text-xs text-gray-500">Company: ${log.company_cost_amount.toFixed(2)}</p>
                            </>
                          ) : (
                            <p className="text-xs text-gray-500">Company: ${log.company_cost_amount.toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => onViewDetails(log.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors group"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        </button>
                        <button
                          onClick={() => onEdit(log.id)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors group"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        </button>
                        <button
                          onClick={() => handleDelete(log.id, log.log_number)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors group"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

          {/* Results Count */}
          <div className="text-center text-sm text-slate-600">
            Showing {filteredLogs.length} of {logs.length} maintenance logs
          </div>
        </>

      {/* Floating Action Bar */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 px-6 py-4 flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                {selectedRows.size}
              </div>
              <span className="font-semibold text-slate-900">
                {selectedRows.size === 1 ? 'Maintenance log selected' : 'Maintenance logs selected'}
              </span>
            </div>
            <div className="h-8 w-px bg-slate-300"></div>
            <button
              onClick={handleBulkEdit}
              className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Edit className="w-5 h-5" />
              <span className="font-medium">Edit</span>
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span className="font-medium">Delete</span>
            </button>
            <button
              onClick={() => setSelectedRows(new Set())}
              className="ml-2 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
